import type { ReactNode } from 'react';

/** The bordered table shared by the Apps and Blog lists. */
export function DataTable({
  columns,
  gridTemplate,
  children,
}: {
  columns: string[];
  gridTemplate: string;
  children: ReactNode;
}) {
  return (
    <div
      className="overflow-hidden rounded-xl border"
      style={{ borderColor: 'var(--line)', background: 'var(--panel)' }}
    >
      <div
        className="panel-head col-head grid gap-3 px-3.5 py-2.5"
        style={{ gridTemplateColumns: gridTemplate }}
      >
        {columns.map((c) => (
          <span key={c}>{c}</span>
        ))}
      </div>
      <div data-rows>{children}</div>
    </div>
  );
}

/**
 * Status as a coloured dot and a word.
 *
 * A pill in v1, a dot in v2 — and the dot is the better of the two here,
 * because a table of pills reads as a table of buttons. Green is live, amber is
 * waiting for a date, grey is neither.
 */
export function StatusDot({ status }: { status: string }) {
  const colour =
    status === 'Published' ? 'var(--ok)' : status === 'Scheduled' ? 'var(--warn)' : 'var(--dim)';

  return (
    <span
      className="inline-flex items-center gap-1.5 text-[11.5px] whitespace-nowrap"
      style={{ color: colour }}
    >
      <span
        aria-hidden="true"
        className="h-[5px] w-[5px] flex-none rounded-full"
        style={{ background: colour }}
      />
      {status}
    </span>
  );
}

/**
 * The row above a table: a filter box, and what the table is showing.
 *
 * The page's title is not here any more — v2 puts it in the sticky header, with
 * the breadcrumb, so it stays on screen while the table scrolls.
 */
export function TableToolbar({
  label,
  value,
  onChange,
  placeholder,
  meta,
  children,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  meta: string;
  children?: ReactNode;
}) {
  return (
    <div className="mb-2.5 flex flex-wrap items-center gap-2.5">
      <label className="block">
        <span className="sr-only">{label}</span>
        <input
          type="search"
          value={value}
          onChange={(event) => onChange(event.target.value)}
          placeholder={placeholder}
          className="field field-filter focus-ring w-50"
        />
      </label>
      {children}
      <span
        className="text-[11px] tracking-normal"
        style={{ color: 'var(--muted)' }}
      >
        {meta}
      </span>
    </div>
  );
}

/**
 * Which page numbers to draw, with gaps where the list is too long to show.
 *
 * Always the first and the last, always the current one and its neighbours,
 * and `null` wherever a run was left out. Exported to be tested on its own:
 * the arithmetic is where an off-by-one hides, and driving it through a
 * rendered table would test the rendering instead.
 */
export function pageNumbers(current: number, count: number): (number | null)[] {
  if (count <= 7) return Array.from({ length: count }, (_, at) => at + 1);

  const shown = new Set([1, count, current, current - 1, current + 1]);
  /*
   * The ends get a third number when the current page is near them, so the row
   * keeps a useful span of pages in reach rather than collapsing to "1 2 … 20"
   * at the very start.
   *
   * It does **not** hold the row to a fixed width — page 3 of 10 draws six
   * buttons and page 4 draws seven — so it still shifts once as somebody walks
   * out of the first group. Saying so rather than claiming otherwise: a fixed
   * seven would need a sliding window, and nobody has asked for one.
   */
  if (current <= 3) shown.add(2).add(3).add(4);
  if (current >= count - 2) shown.add(count - 1).add(count - 2).add(count - 3);

  const pages = [...shown].filter((page) => page >= 1 && page <= count).sort((a, b) => a - b);

  const withGaps: (number | null)[] = [];
  for (const [at, page] of pages.entries()) {
    const previous = pages[at - 1];
    if (at > 0) {
      const missing = page - previous - 1;
      // One missing page is drawn as itself: "1 … 3" is longer than "1 2 3"
      // and says less. A gap is only worth its ambiguity for a real run.
      if (missing === 1) withGaps.push(previous + 1);
      else if (missing > 1) withGaps.push(null);
    }
    withGaps.push(page);
  }
  return withGaps;
}

/**
 * Page numbers and a total, under a table.
 *
 * Numbers rather than "load more", because this is a working screen: somebody
 * needs to know how many there are and get to one of them, and an endless
 * scroll answers neither question. Renders nothing at all when everything fits
 * on one page — a pager offering page 1 of 1 is a control that cannot do
 * anything.
 */
export function Pager({
  page,
  perPage,
  total,
  onPage,
  noun,
}: {
  page: number;
  perPage: number;
  total: number;
  onPage: (page: number) => void;
  /** Singular; pluralised here so both tables say it the same way. */
  noun: string;
}) {
  const count = Math.ceil(total / perPage);
  if (count <= 1) return null;

  /*
   * Clamped, because `page` can outlive the total it was chosen against: 51
   * apps, somebody on page 2, one gets discontinued. Unclamped the line reads
   * "101–100 of 100" — a range running backwards, over rows that were never
   * there. The page itself is put back by whoever owns it; this makes sure the
   * sentence is never false in the meantime.
   */
  const showing = Math.min(page, count);
  const first = (showing - 1) * perPage + 1;
  const last = Math.min(showing * perPage, total);

  return (
    <nav
      aria-label={`${noun} pages`}
      className="mt-2.5 flex flex-wrap items-center justify-between gap-2.5"
    >
      <span className="text-[11px] tracking-normal" style={{ color: 'var(--muted)' }}>
        {/*
          Always plural: this only renders past one page, so `total` is greater
          than `perPage` and therefore at least 2. A singular branch here would
          be a line no test could ever reach.
        */}
        {first}–{last} of {total} {noun}s
      </span>
      <div className="flex flex-wrap items-center gap-1">
        <button
          type="button"
          className="btn-small focus-ring cursor-pointer disabled:cursor-default disabled:opacity-40"
          disabled={page <= 1}
          onClick={() => onPage(page - 1)}
        >
          Previous
        </button>
        {pageNumbers(page, count).map((one, at) =>
          one === null ? (
            // Not a button and not read out: it stands for the numbers that are
            // not drawn, and there is nothing here to press.
            <span
              key={`gap-${at}`}
              aria-hidden="true"
              className="px-1 text-[11px]"
              style={{ color: 'var(--dim)' }}
            >
              …
            </span>
          ) : (
            <button
              key={one}
              type="button"
              // The current page is named as such rather than only coloured, so
              // it is answerable to somebody who cannot see which one is lit.
              aria-current={one === page ? 'page' : undefined}
              aria-label={`Page ${one}`}
              className="btn-small focus-ring cursor-pointer"
              style={
                one === page
                  ? { background: 'var(--accent)', color: 'var(--on-accent)' }
                  : undefined
              }
              onClick={() => onPage(one)}
            >
              {one}
            </button>
          ),
        )}
        <button
          type="button"
          className="btn-small focus-ring cursor-pointer disabled:cursor-default disabled:opacity-40"
          disabled={page >= count}
          onClick={() => onPage(page + 1)}
        >
          Next
        </button>
      </div>
    </nav>
  );
}
